import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import { getStudents, getWaiting } from './api/client';
import './styles/global.css';

// Lazy load pages
const LoginPage    = lazy(() => import('./pages/LoginPage'));
const Dashboard    = lazy(() => import('./pages/Dashboard'));
const WaitingPage  = lazy(() => import('./pages/WaitingPage'));
const StudentsPage = lazy(() => import('./pages/StudentsPage'));
const FamiliesPage = lazy(() => import('./pages/FamiliesPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const ButPage      = lazy(() => import('./pages/ButPage'));
const ReportsPage  = lazy(() => import('./pages/ReportsPage'));
const CustomizingPage = lazy(() => import('./pages/CustomizingPage'));
const UsersPage    = lazy(() => import('./pages/UsersPage'));
const ImportPage   = lazy(() => import('./pages/ImportPage'));

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace/>;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace/>;
  return children;
}

function AppShell() {
  const { user } = useAuth();
  const location = useLocation();
  const [counts, setCounts] = useState({ waiting: 0, students: 0 });

  useEffect(() => {
    if (!user) return;
    Promise.all([getStudents(), getWaiting()]).then(([sr, wr]) => {
      setCounts({
        students: sr.data.filter(s => s.status === 'active').length,
        waiting: wr.data.length,
      });
    }).catch(() => {});
  }, [user, location.pathname]);

  if (!user) return <Navigate to="/login" replace/>;

  return (
    <div className="app-shell">
      <Sidebar counts={counts}/>
      <main className="main">
        <Suspense fallback={<div className="page-loading"><div className="spinner"/></div>}>
          <Routes>
            <Route path="/dashboard"    element={<ProtectedRoute><Dashboard/></ProtectedRoute>}/>
            <Route path="/waiting"      element={<ProtectedRoute><WaitingPage/></ProtectedRoute>}/>
            <Route path="/students"     element={<ProtectedRoute><StudentsPage/></ProtectedRoute>}/>
            <Route path="/families"     element={<ProtectedRoute><FamiliesPage/></ProtectedRoute>}/>
            <Route path="/payments"     element={<ProtectedRoute roles={['admin','director','accountant','parent']}><PaymentsPage/></ProtectedRoute>}/>
            <Route path="/but"          element={<ProtectedRoute roles={['admin','director','accountant']}><ButPage/></ProtectedRoute>}/>
            <Route path="/reports"      element={<ProtectedRoute roles={['admin','director','accountant']}><ReportsPage/></ProtectedRoute>}/>
            <Route path="/customizing"  element={<ProtectedRoute roles={['admin']}><CustomizingPage/></ProtectedRoute>}/>
            <Route path="/users"        element={<ProtectedRoute roles={['admin']}><UsersPage/></ProtectedRoute>}/>
            <Route path="/import"       element={<ProtectedRoute roles={['admin']}><ImportPage/></ProtectedRoute>}/>
            <Route path="*"             element={<Navigate to="/dashboard" replace/>}/>
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" toastOptions={{
          style: { fontFamily:'DM Sans, sans-serif', fontSize:'13px', borderRadius:'8px', border:'1px solid var(--border)' }
        }}/>
        <Suspense fallback={<div className="page-loading"><div className="spinner"/></div>}>
          <Routes>
            <Route path="/login" element={<LoginWithRedirect/>}/>
            <Route path="/*"     element={<AppShell/>}/>
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

function LoginWithRedirect() {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace/>;
  return <LoginPage/>;
}
